import type { NestedActionLabourResponse, FetchAssignmentResponse } from "src/api/models";

/**
 * Maps assignment types to their corresponding severity levels for UI display
 */
export const TYPE_SEVERITY_MAP: Record<string, string> = {
  actual: "success",
  offer: "warning",
  extra: "danger",
};

/**
 * Calculates total hours from an array of labor entries
 */
export const calculateTotalHours = (actionLabours?: NestedActionLabourResponse[] | null): number => {
  if (!actionLabours) return 0;
  return actionLabours.reduce((sum: number, labor: NestedActionLabourResponse) => sum + (labor.total_hours || 0), 0);
};

/**
 * Returns the severity level for a given assignment type
 */
export const getSeverityForType = (type: string): string => {
  return TYPE_SEVERITY_MAP[type] || "info";
};

/**
 * Capitalizes the first letter of a string
 */
export const capitalize = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Extracts unique labor types from assignments
 */
export const getUniqueTypes = (assignments: FetchAssignmentResponse[]): string[] => {
  if (!Array.isArray(assignments)) return [];

  const types = new Set<string>();

  assignments.forEach((assignment) => {
    (assignment.action_labours || []).forEach((labor) => {
      if (labor.type) types.add(String(labor.type));
    });
  });

  return Array.from(types);
};

/**
 * Calculates total hours by type from assignments
 */
export const calculateHoursByType = (assignments: FetchAssignmentResponse[], uniqueTypes: string[]): Record<string, number> => {
  if (!Array.isArray(assignments)) {
    return { total: 0 };
  }

  const result: Record<string, number> = { total: 0 };

  // Initialize all found types with 0
  uniqueTypes.forEach((type) => {
    result[type] = 0;
  });

  assignments.forEach((assignment) => {
    (assignment.action_labours || []).forEach((labor) => {
      const hours = labor.total_hours || 0;
      const type = String(labor.type || "");

      // Add to type counter if type exists
      if (type && type in result) {
        result[type] += hours;
      }

      // Add to total regardless
      result.total += hours;
    });
  });

  return result;
};

/**
 * Gets CSS classes for severity levels
 */
export const getSeverityClasses = (severity: string): Record<string, boolean> => ({
  'bg-opacity-30 dark:bg-opacity-30 border rounded px-3 py-1': true,
  'bg-green-100 dark:bg-green-900 border-green-200 dark:border-green-600 text-green-600 dark:text-green-400': severity === 'success',
  'bg-yellow-100 dark:bg-yellow-900 border-yellow-200 dark:border-yellow-600 text-yellow-600 dark:text-yellow-400': severity === 'warning',
  'bg-red-100 dark:bg-red-900 border-red-200 dark:border-red-600 text-red-600 dark:text-red-400': severity === 'danger',
  'bg-blue-100 dark:bg-blue-900 border-blue-200 dark:border-blue-600 text-blue-600 dark:text-blue-400': severity === 'info'
});
